"use client";
import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import {
  GraduationCap,
  MapPin,
  Globe,
  XCircle,
  Calendar,
  School,
  Edit2,
  Trash2,
  MoreVertical,
  Plus,
} from "lucide-react";
import Header from "../../../components/admin/common/Header";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Sidebar from "../../../components/admin/common/Sidebar";
import {
  getAllEducationsAdmin,
  updateEducationAdmin,
  deleteEducationAdmin,
} from "../../../services/educationServices";
import DeleteConfirmationModal from "../../../components/admin/members/DeleteMemberModal";
import EditEducationModal from "../../../components/admin/education/EditEducationModal";
import { getAllDegrees } from "../../../services/degreeServices";
import { useUserModulePermissions } from "../../../hooks/useUserModulePermissions";
import { getCookie } from "../../../utils";

// Status badge component
const StatusBadge = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return {
          label: "Pending",
          color: "#f59e0b",
          bgColor: "#fef3c7",
          textColor: "#92400e",
        };
      case "approved":
        return {
          label: "Approved",
          color: "#10b981",
          bgColor: "#d1fae5",
          textColor: "#065f46",
        };
      case "rejected":
        return {
          label: "Rejected",
          color: "#ef4444",
          bgColor: "#fee2e2",
          textColor: "#991b1b",
        };
      default:
        return {
          label: "Unknown",
          color: "#6b7280",
          bgColor: "#f3f4f6",
          textColor: "#374151",
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium"
      style={{
        backgroundColor: config.bgColor,
        color: config.textColor,
      }}
    >
      <div
        className="w-1.5 h-1.5 rounded-full mr-1.5"
        style={{ backgroundColor: config.color }}
      />
      {config.label}
    </span>
  );
};

// Active status badge component
const ActiveBadge = ({ isActive }) => {
  const config = isActive
    ? {
        label: "Active",
        bgColor: "#d1fae5",
        textColor: "#065f46",
        dotColor: "#10b981",
      }
    : {
        label: "Inactive",
        bgColor: "#fee2e2",
        textColor: "#991b1b",
        dotColor: "#ef4444",
      };

  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium"
      style={{
        backgroundColor: config.bgColor,
        color: config.textColor,
      }}
    >
      <div
        className="w-1.5 h-1.5 rounded-full mr-1.5"
        style={{ backgroundColor: config.dotColor }}
      />
      {config.label}
    </span>
  );
};

const ActionMenu = ({ onEdit, onDelete, canEdit, canDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (e) => {
    e.stopPropagation();

    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 120 + window.scrollX, // 120px is menu width
      });
    }

    setIsOpen(!isOpen);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    onEdit();
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    onDelete();
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded-md hover:bg-gray-100"
      >
        <MoreVertical size={14} />
      </button>

      {isOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={menuRef}
            className="bg-white border border-gray-200 rounded-lg shadow-lg min-w-[120px]"
            style={{
              position: "fixed",
              top: buttonPosition.top,
              left: buttonPosition.left,
              zIndex: 10000,
            }}
          >
            {canEdit && (
              <button
                onClick={handleEdit}
                className="w-full text-left px-2.5 py-1.5 text-xs flex items-center gap-1.5 text-green first:rounded-t-lg"
              >
                <Edit2 size={12} className="text-green-600" />
                Edit
              </button>
            )}

            {canDelete && (
              <button
                onClick={handleDelete}
                className="w-full text-left px-2.5 py-1.5 text-xs  flex items-center gap-1.5 text-red last:rounded-b-lg"
              >
                <Trash2 size={12} className="text-red-600" />
                Delete
              </button>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

const EducationTable = () => {
  const [educations, setEducations] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedEducation, setSelectedEducation] = useState(null);

  const queryClient = useQueryClient();

  // Fetch educations
  const {
    data: educationData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["educations-admin"],
    queryFn: getAllEducationsAdmin,
  });

  const { data: degreesData } = useQuery({
    queryKey: ["degrees-admin"],
    queryFn: getAllDegrees,
  });

  const updateEducationMutation = useMutation({
    mutationFn: ({ id, updateData }) => updateEducationAdmin(id, updateData),
    onMutate: async ({ id, updateData }) => {
      await queryClient.cancelQueries({ queryKey: ["educations-admin"] });

      const previousEducations = queryClient.getQueryData(["educations-admin"]);

      queryClient.setQueryData(["educations-admin"], (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((edu) =>
            edu._id === id ? { ...edu, ...updateData } : edu
          ),
        };
      });

      return { previousEducations };
    },
    onError: (err, { id, updateData }, context) => {
      queryClient.setQueryData(
        ["educations-admin"],
        context.previousEducations
      );
      console.error("Error updating education:", err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["educations-admin"] });
    },
    onSuccess: () => {
      setEditModalOpen(false);
      setSelectedEducation(null);
      console.log("Education updated successfully");
    },
  });

  // Delete education mutation
  const deleteEducationMutation = useMutation({
    mutationFn: (id) => deleteEducationAdmin(id),
    onMutate: async (educationId) => {
      await queryClient.cancelQueries({ queryKey: ["educations-admin"] });

      const previousEducations = queryClient.getQueryData(["educations-admin"]);

      queryClient.setQueryData(["educations-admin"], (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.filter((edu) => edu._id !== educationId),
        };
      });

      return { previousEducations };
    },
    onError: (err, educationId, context) => {
      queryClient.setQueryData(
        ["educations-admin"],
        context.previousEducations
      );
      console.error("Error deleting education:", err);
    },
    onSuccess: () => {
      setDeleteModalOpen(false);
      setSelectedEducation(null);
      console.log("Education deleted successfully");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["educations-admin"] });
    },
  });

  useEffect(() => {
    if (educationData?.data) {
      setEducations(educationData.data);
    }
  }, [educationData?.data]);

  const handleEdit = (education) => {
    setSelectedEducation(education);
    setEditModalOpen(true);
  };

  const handleDelete = (education) => {
    setSelectedEducation(education);
    setDeleteModalOpen(true);
  };

  const handleEditSave = (educationId, formData) => {
    updateEducationMutation.mutate({
      id: educationId,
      updateData: formData,
    });
  };

  const handleDeleteConfirm = () => {
    if (selectedEducation) {
      deleteEducationMutation.mutate(selectedEducation._id);
    }
  };

  const role = getCookie("role");
  const userId = getCookie("userId");

  const { data: permissionsData } = useUserModulePermissions(
    "Education",
    userId,
    {
      enabled: role === "maintainer" && userId && userId !== "undefined",
    }
  );

  let canEdit = false;
  let canDelete = false;

  if (role === "admin") {
    canEdit = true;
    canDelete = true;
  } else if (role === "maintainer" && permissionsData?.data?.permissions) {
    const modulePermissions = permissionsData?.data?.permissions.find(
      (p) => p.module === "Education"
    );

    console.log("modulePermissions found:", modulePermissions);

    if (modulePermissions) {
      canEdit = modulePermissions.edit === true;
      canDelete = modulePermissions.delete === true;
    }
  }
  const showActions = canEdit || canDelete;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
        <div className="h-full">
          <div className="sticky top-0 h-screen text-white">
            <Sidebar />
          </div>
        </div>
        <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
          <PageWrapper>
            <div className="flex flex-col h-full">
              <div className="flex-1 p-6 overflow-y-auto">
                <Header
                  title="Education Management"
                  desc="Loading educations..."
                />
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4f22d8]"></div>
                </div>
              </div>
            </div>
          </PageWrapper>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
        <div className="h-full">
          <div className="sticky top-0 h-screen text-white">
            <Sidebar />
          </div>
        </div>
        <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
          <PageWrapper>
            <div className="flex flex-col h-full">
              <div className="flex-1 p-6 overflow-y-auto">
                <Header
                  title="Education Management"
                  desc="Error loading educations"
                />
                <div className="flex items-center justify-center py-12">
                  <div className="text-red-500 flex items-center gap-2">
                    <XCircle size={20} />
                    <span>Error loading educations: {error?.message}</span>
                  </div>
                </div>
              </div>
            </div>
          </PageWrapper>
        </div>
      </div>
    );
  }

  const displayEducations =
    educations.length > 0 ? educations : educationData?.data || [];

  return (
    <>
      <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
        <div className="h-full">
          <div className="sticky top-0 h-screen text-white">
            <Sidebar />
          </div>
        </div>

        <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
          <PageWrapper>
            <div className="flex flex-col h-full">
              <div className="flex-1 p-6 overflow-hidden flex flex-col">
                <Header
                  title="Education Management"
                  desc="Manage educational qualifications and degrees"
                />

                {/* Table Container with Fixed Height */}
                <div className="mt-6 flex-1 flex flex-col overflow-hidden">
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col h-full">
                    {/* Table Header */}
                    <div className="p-4 border-b border-gray-100 flex-shrink-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-[#4f22d8] bg-opacity-10 rounded-lg">
                            <GraduationCap
                              size={18}
                              className="text-[#4f22d8]"
                            />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              Education Records
                            </h3>
                            <p className="text-sm text-gray-500">
                              Total {displayEducations.length} record(s)
                            </p>
                          </div>
                        </div>
                        {canEdit && (
                          <button
                            onClick={() => {
                              if (typeof window !== "undefined") {
                                window.location.href = "/admin/education/add";
                              }
                            }}
                            className="px-3 py-2 bg-[#4f22d8] text-white rounded-lg hover:bg-[#4f22d8]/90 transition-colors flex items-center gap-2 text-sm"
                          >
                            <Plus size={14} />
                            Add Education
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Table Content */}
                    {displayEducations.length === 0 ? (
                      <div className="flex-1 flex flex-col items-center justify-center p-12">
                        <GraduationCap
                          size={48}
                          className="mx-auto text-gray-300 mb-4"
                        />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          No education records
                        </h3>
                        <p className="text-gray-500 text-sm">
                          Get started by adding your first education record.
                        </p>
                      </div>
                    ) : (
                      <div className="flex-1 overflow-hidden flex flex-col">
                        <div className="overflow-x-auto overflow-y-auto flex-1">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                              <tr>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                  Education Details
                                </th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                  Institution
                                </th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                  Location
                                </th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                  Active
                                </th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                  Created
                                </th>
                                {showActions && (
                                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                    Actions
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {displayEducations.map((education) => (
                                <tr
                                  key={education._id}
                                  className="hover:bg-gray-50/50 transition-colors"
                                >
                                  {/* Education Details */}
                                  <td className="py-3 px-4">
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <GraduationCap
                                          size={14}
                                          className="text-[#4f22d8]"
                                        />
                                        <span className="font-semibold text-gray-900 text-sm">
                                          {education.degree?.name || "N/A"}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2 text-xs text-gray-600">
                                        <School
                                          size={12}
                                          className="text-gray-400"
                                        />
                                        <span>{education.course}</span>
                                      </div>
                                    </div>
                                  </td>

                                  {/* Institution */}
                                  <td className="py-3 px-4">
                                    <div className="space-y-1">
                                      <div className="font-medium text-gray-900 text-sm">
                                        {education.university}
                                      </div>
                                      {education.website && (
                                        <a
                                          href={education.website}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-xs text-[#4f22d8] hover:underline flex items-center gap-1"
                                        >
                                          <Globe size={10} />
                                          Visit Website
                                        </a>
                                      )}
                                    </div>
                                  </td>

                                  {/* Location */}
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                                      <MapPin
                                        size={12}
                                        className="text-gray-400"
                                      />
                                      <span>{education.place}</span>
                                    </div>
                                  </td>

                                  {/* Status - Static Badge */}
                                  <td className="py-3 px-4">
                                    <StatusBadge status={education.status} />
                                  </td>

                                  {/* Active - Static Badge */}
                                  <td className="py-3 px-4">
                                    <ActiveBadge
                                      isActive={education.isActive}
                                    />
                                  </td>

                                  {/* Created Date */}
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-2 text-xs text-gray-600">
                                      <Calendar
                                        size={12}
                                        className="text-gray-400"
                                      />
                                      <span>
                                        {formatDate(education.createdAt)}
                                      </span>
                                    </div>
                                  </td>

                                  {showActions && (
                                    <>
                                      {/* Actions */}
                                      <td className="py-3 px-4 text-center">
                                        <ActionMenu
                                          onEdit={() => handleEdit(education)}
                                          onDelete={() =>
                                            handleDelete(education)
                                          }
                                          canEdit={canEdit}
                                          canDelete={canDelete}
                                        />
                                      </td>
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </PageWrapper>
        </div>
      </div>

      {editModalOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <EditEducationModal
            isOpen={editModalOpen}
            onRequestClose={() => {
              setEditModalOpen(false);
              setSelectedEducation(null);
            }}
            onSave={handleEditSave}
            education={selectedEducation}
            degrees={degreesData?.data || []}
            isLoading={updateEducationMutation.isPending}
          />,
          document.body
        )}

      {deleteModalOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <DeleteConfirmationModal
            isOpen={deleteModalOpen}
            onRequestClose={() => {
              setDeleteModalOpen(false);
              setSelectedEducation(null);
            }}
            onDelete={handleDeleteConfirm}
            itemName={
              selectedEducation
                ? `${selectedEducation.degree?.name || "N/A"} - ${
                    selectedEducation.course
                  }`
                : ""
            }
            itemType="education record"
            isLoading={deleteEducationMutation.isPending}
          />,
          document.body
        )}
    </>
  );
};

export default EducationTable;
